import PropTypes from 'prop-types';
import classNames from 'classnames';

import { isDDEnv, getContent } from '@/utils/helper';

import Text from '@/atoms/Text/Text';
import { CtaWrapper } from '@/molecules/CTA/index';

import { TEXT_BASE_CLASS } from '@/atoms/Text/Text.constants';
import { CtfRichTextTag } from '@/utils/constants';

export default function Paragraph({
  contents,
  invert,
  isBlockquote = false,
  isBlockquoteBodyLarge,
  isDiv,
  style = {},
  entries,
}) {
  if (!contents?.length) return null;

  const { blockquoteFont, customFont, paragraphWrapper } = style;

  const paragraphContent = contents.map((content, index) => {
    /// link
    if (
      content?.nodeType === CtfRichTextTag.HYPERLINK ||
      content?.nodeType === CtfRichTextTag.ENTRY_HYPERLINK
    ) {
      const url =
        getContent(content?.data?.target?.sys?.id, entries)?.fields?.slug ??
        content?.data?.uri;

      return (
        <CtaWrapper
          url={url}
          key={index}
          name={content?.content?.[0]?.value}
          classNames="inline-flex"
        >
          <Text
            tag={isDDEnv() ? 'small' : 'button'}
            style="dd-en:underline dd-kr:underline dd-en:underline-offset-[5px] dd-kr:underline-offset-[5px] dd-kr:decoration-[1.5px]  dd-en:decoration-[1.5px] dd-en:decoration-actual-dd-gold dd-kr:decoration-actual-dd-gold"
          >
            {content?.content?.[0]?.value}
          </Text>
        </CtaWrapper>
      );
    }

    /// italic | bold | underline
    const type = content?.marks?.[0]?.type;
    const tag = isBlockquote
      ? 'blockquote'
      : type === 'italic'
      ? 'button'
      : 'body';
    return (
      <Text
        style={getParagraphTextClass({
          [type]: !!type,
          isBlockquote,
          isBlockquoteBodyLarge,
          invert,
          blockquoteFont,
          customFont,
        })}
        key={index}
        tag={tag}
        isBlockquote={isBlockquote}
        custom
      >
        {content?.value}
      </Text>
    );
  });

  const wrapperClasses = classNames({
    'aaco-paragraph aaco-paragraph--is-blockquote md:dd-kr:mb-5 md:dd-en:mb-5':
      isBlockquote && !isBlockquoteBodyLarge,
    'aaco-paragraph aaco-paragraph--is-blockquote-body-large':
      isBlockquoteBodyLarge,
    'aaco-paragraph aaco-paragraph--is-text leading-[18.2px]': !isBlockquote,
    [paragraphWrapper]: true,
  });

  if (isDiv) {
    return <div className={wrapperClasses}>{paragraphContent}</div>;
  }

  return <p className={wrapperClasses}>{paragraphContent}</p>;
}

export function HeadingParagraph({ contents, textTag, entries }) {
  if (!contents?.length) return null;

  return (
    <div className="aaco-paragraph aaco-paragraph--is-heading w-full md:flex">
      {contents.map((content, index) => {
        /// link
        if (
          content?.nodeType === CtfRichTextTag.HYPERLINK ||
          content?.nodeType === CtfRichTextTag.ENTRY_HYPERLINK
        ) {
          const url =
            getContent(content?.data?.target?.sys?.id, entries)?.fields?.slug ??
            content?.data?.uri;

          return (
            <CtaWrapper
              url={url}
              key={index}
              name={content?.content?.[0]?.value}
              classNames="inline-flex underline underline-offset-[5px] decoration-[1.5px]"
            >
              <Text tag={textTag}>{content?.content?.[0]?.value}</Text>
            </CtaWrapper>
          );
        }

        /// italic | bold | underline
        const type = content?.marks?.[0]?.type;
        const tag = type === 'italic' ? 'button' : 'body';
        return (
          <Text key={index} tag={textTag ?? tag} style="mr-2">
            {content?.value}
          </Text>
        );
      })}
    </div>
  );
}

Paragraph.propTypes = {
  content: PropTypes.array,
  isBlockquote: PropTypes.bool,
  style: PropTypes.shape({
    blockquoteFont: PropTypes.string,
    customFont: PropTypes.string,
    paragraphWrapper: PropTypes.string,
  }),
};

function getParagraphTextClass({
  italic,
  bold,
  underline,
  invert,
  isBlockquote,
  isBlockquoteBodyLarge,
  blockquoteFont,
  customFont,
}) {
  // set color
  let color = 'text-black';
  if (invert && isBlockquote) {
    color = 'text-black';
  } else if (invert) {
    color = 'text-acacia';
  } else if (isBlockquote || underline) {
    color = '';
  }

  // set font
  let font = '';
  if (isBlockquoteBodyLarge) {
    font = TEXT_BASE_CLASS['blockquote-body-large'];
  } else if (isBlockquote && blockquoteFont) {
    font = blockquoteFont;
  } else if (isBlockquote) {
    font = TEXT_BASE_CLASS['blockquote'];
  } else if (bold) {
    font = `${TEXT_BASE_CLASS['body-bold']}`;
    font += customFont ?? '';
  } else if (customFont) {
    font = customFont;
  } else if (underline) {
    font = TEXT_BASE_CLASS['button'];
  } else {
    font = `${TEXT_BASE_CLASS.body}`;
  }

  return classNames('whitespace-pre-line', color, font, {
    italic: italic,
    'underline underline-offset-[5px] decoration-[1.5px] decoration-violet-bright':
      underline,
  });
}
